import { useState } from 'react'
import { CheckCircle } from '@phosphor-icons/react'
import { useActor } from '@/auth/pin/phoneSession'
import { useFormat, useI18n } from '@/i18n'
import { enqueueCommand } from '@/offline'
import { Banner, BilingualLabel, Button, Card, CardBody, CardHeader, Field, PhotoCapture, useToast } from '@/ui'
import type { ConfirmCleanoutPayload } from '../../commands'
import '../../commands'
import { runParentIds, type ProcessState, type RunView } from '../../projection'
import { canCancel } from '../../model/rules'
import { YesNo } from '../../components/bits'
import { useDraftState } from '../../useProcess'
import { CancelRun } from './CancelRun'
import { DEFAULT_PHOTO_POINTS } from './draft'

interface CleanoutDraft {
  photos: Record<string, string | null>
  allergen_done: boolean | null
  allergen_photo: string | null
  empty: boolean
}

/** F6.2 — Machine empty with a photo per point, and the full allergen clean when the crop changed. */
export function CleanoutSection({
  state,
  run,
  onDone,
}: {
  state: ProcessState
  run: RunView
  onDone: () => void
}) {
  const { t, locale } = useI18n()
  const fmt = useFormat()
  const toast = useToast()
  const actor = useActor()
  const step = state.steps.get(run.step_code)
  const points = step?.photo_points.length ? step.photo_points : DEFAULT_PHOTO_POINTS
  const [d, update, loaded, clear] = useDraftState<CleanoutDraft>(
    `process.cleanout:${run.id}`,
    'process.cleanout',
    () => ({ photos: {}, allergen_done: null, allergen_photo: null, empty: false }),
    actor.user_id,
  )
  const [saving, setSaving] = useState(false)

  if (run.cleanout_confirmed_at)
    return (
      <Card>
        <CardBody className="flex items-center gap-3 py-4">
          <CheckCircle className="text-[28px] text-ok" weight="fill" aria-hidden="true" />
          <div>
            <BilingualLabel k="process.cleanout.confirmed" className="font-bold" />
            <div className="text-[13px] text-muted">{fmt.dateTime(run.cleanout_confirmed_at)}</div>
          </div>
        </CardBody>
      </Card>
    )
  if (!loaded) return null

  const photosDone = points.every((p) => !!d.photos[p.key])
  const allergenDone = !run.allergen_clean_required || (d.allergen_done === true && !!d.allergen_photo)
  const canTickEmpty = photosDone && allergenDone

  const confirm = async () => {
    if (!canTickEmpty || !d.empty) return
    setSaving(true)
    try {
      const photo_ids = points.map((p) => d.photos[p.key]!)
      const payload: ConfirmCleanoutPayload = {
        run_id: run.id,
        photo_ids,
        photo_points: points.map((p) => p.key),
        allergen_clean_required: run.allergen_clean_required,
        allergen_clean: run.allergen_clean_required ? d.allergen_done === true : false,
        allergen_photo_id: run.allergen_clean_required ? d.allergen_photo : null,
      }
      await enqueueCommand({
        type: 'confirm_cleanout',
        payload,
        actorId: actor.user_id,
        parentIds: runParentIds(run),
        mediaIds: [...photo_ids, ...(payload.allergen_photo_id ? [payload.allergen_photo_id] : [])],
      })
      await clear()
      toast.success(t('process.cleanout.saved'))
      onDone()
    } catch (e) {
      toast.error(t('process.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Banner tone="info">{t('process.cleanout.intro')}</Banner>
      {run.allergen_clean_required && (
        <Card>
          <CardHeader eyebrow={<BilingualLabel k="process.cleanout.allergenTitle" />} />
          <CardBody className="flex flex-col gap-3">
            <p className="text-[14px] text-muted">
              {run.previous_run?.crop_name
                ? t('process.cleanout.allergenWhyCrop', { crop: run.previous_run.crop_name })
                : t('process.cleanout.allergenWhyUnknown')}
            </p>
            <Field label={<BilingualLabel k="process.cleanout.allergenPhoto" />} required>
              <PhotoCapture value={d.allergen_photo} onChange={(id) => update((x) => ({ ...x, allergen_photo: id }))} />
            </Field>
            <Field label={<BilingualLabel k="process.cleanout.allergenDone" />} required>
              <YesNo value={d.allergen_done} onChange={(v) => update((x) => ({ ...x, allergen_done: v }))} />
            </Field>
          </CardBody>
        </Card>
      )}
      <Card>
        <CardHeader eyebrow={<BilingualLabel k="process.cleanout.photosTitle" />} />
        <CardBody className="flex flex-col gap-3">
          {points.map((p) => (
            <Field key={p.key} label={<BilingualLabel hi={p.hi} en={p.en} />} required>
              <PhotoCapture
                value={d.photos[p.key] ?? null}
                onChange={(id) => update((x) => ({ ...x, photos: { ...x.photos, [p.key]: id } }))}
              />
            </Field>
          ))}
        </CardBody>
      </Card>
      <Field
        label={<BilingualLabel k="process.cleanout.empty" />}
        hint={canTickEmpty ? undefined : t('process.cleanout.emptyHint')}
        required
      >
        <YesNo
          value={d.empty ? true : null}
          disabled={!canTickEmpty}
          onChange={(v) => update((x) => ({ ...x, empty: v }))}
        />
      </Field>
      {run.flush_required && (
        <Banner tone="warn" title={<BilingualLabel k="process.flush.title" />}>
          {t('process.flush.afterCleanout', { kg: fmt.weight(run.flush_min_g ?? 0) })}
        </Banner>
      )}
      {!run.flush_required && run.stream === 'MAIN' && locale && (
        <p className="text-[13px] text-muted">{t('process.flush.notNeeded')}</p>
      )}
      <Button size="xl" fullWidth disabled={!canTickEmpty || !d.empty} loading={saving} onClick={() => void confirm()}>
        <BilingualLabel k="process.cleanout.confirm" />
      </Button>
      {canCancel(run) && <CancelRun run={run} />}
    </div>
  )
}
